import React, { memo, useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import Const from "../assets/images/const.webp";

const HeroSection = memo(() => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const [isLoaded, setIsLoaded] = useState(false);
  
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section className="relative h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        <img 
          src={Const}
          alt="Corporate Building"
          className={`w-full h-full object-cover transition-all duration-1500 ${
            inView && isLoaded ? 'scale-100 blur-0' : 'scale-125 blur-sm'
          }`}
        />
        <div className={`absolute inset-0 bg-gradient-to-b from-red-900/80 to-black/70 
          transition-all duration-1500 transform ${
          inView && isLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-110'
        }`} />
      </div>
      <div 
        ref={ref} 
        className="relative z-10 text-center text-white px-4 space-y-6"
      >
        <h1 className={`text-5xl font-extrabold leading-tight drop-shadow-lg sm:text-6xl
          transition-all duration-1000 transform ${
          inView && isLoaded ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-16 scale-90'
        }`}>
          Our Journey
        </h1>
        <p className={`text-xl text-gray-200 transition-all duration-1000 delay-300 transform ${
          inView && isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-16'
        }`}>
          Shaping the Future Since 1987
        </p>
      </div>
    </section>
  );
});

function Card({ children, className, inView, index }) {
  return (
    <div className={`bg-white/95 backdrop-blur p-6 md:p-8 rounded-xl shadow-lg border-2 border-red-100 
      transition-all duration-1000 transform
      ${inView ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-16 scale-95'}
      hover:shadow-xl hover:border-red-200 hover:-translate-y-2 ${className}`}
      style={{ transitionDelay: `${index * 100}ms` }}>
      {children}
    </div>
  );
}

function TimelineNode({ icon, inView, index }) {
  return (
    <div className={`relative z-10 w-16 h-16 rounded-full bg-gradient-to-br from-red-600 to-red-900 
      text-white text-xl flex items-center justify-center shadow-lg mb-8
      transition-all duration-1000 transform
      ${inView ? 'opacity-100 scale-100 rotate-0' : 'opacity-0 scale-50 rotate-180'}`}
      style={{ transitionDelay: `${index * 200}ms` }}>
      <span className="animate-bounce hover:animate-ping">{icon}</span>
    </div>
  );
}

function TimelineConnector({ inView, progress }) {
  return (
    <div className="absolute left-1/2 h-full w-1 transform -translate-x-1/2 rounded-full overflow-hidden">
      <div className={`h-full bg-gradient-to-b from-red-700 to-red-900 
        transition-all duration-1500 ease-out
        origin-top`}
        style={{ transform: `scaleY(${progress})` }} />
    </div>
  );
}

function TimelineItem({ item, index }) {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <div ref={ref} className="relative">
      <div className="flex flex-col items-center">
        <TimelineNode icon={item.icon} inView={inView} index={index} />
        <div className="w-full max-w-2xl">
          <Card inView={inView} index={index}>
            <div className="text-center">
              <h3 className={`text-2xl font-bold mb-2 text-red-900 transition-all duration-1000
                transform ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                style={{ transitionDelay: `${index * 150}ms` }}>
                {item.title}
              </h3>
              <div className={`inline-block px-4 py-1 rounded-full bg-red-100 text-red-900 
                text-sm font-semibold mb-4 transition-all duration-1000
                transform ${inView ? 'opacity-100 scale-100' : 'opacity-0 scale-0'}`}
                style={{ transitionDelay: `${index * 200}ms` }}>
                {item.year}
              </div>
              <ul className="space-y-4">
                {item.content.map((line, lineIndex) => (
                  <li
                    key={lineIndex}
                    className={`flex items-center gap-3 text-gray-700 text-base justify-center
                      transition-all duration-700 transform
                      ${inView ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-16'}`}
                    style={{ transitionDelay: `${(index * 100) + (lineIndex * 150)}ms` }}
                  >
                    <span className="text-red-600 text-lg animate-pulse hover:animate-ping">•</span>
                    <span className="hover:text-red-600 transition-colors duration-300">{line}</span>
                  </li>
                ))}
              </ul>
              {item.action && (
                <div 
                  className={`mt-6 transition-all duration-1000 transform
                    ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                  style={{ transitionDelay: `${(index * 100) + (item.content.length * 150)}ms` }}
                >
                  <button
                    onClick={() => window.location.href = item.action.link}
                    className="px-6 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-full 
                      font-semibold shadow-lg hover:shadow-xl transition-all duration-300 
                      transform hover:-translate-y-1 hover:scale-105
                      focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50"
                  >
                    {item.action.text}
                  </button>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Timeline() {
  const [scrollProgress, setScrollProgress] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const progress = Math.min(scrollTop / (documentHeight - windowHeight), 1);
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const timelineData = [
    {
      id: 1,
      title: "Our Foundation",
      content: [
        "RIPL was conceived in 1987 as 'Ray Consultants'",
        "Pioneering consultancy in Civil Engineering",
        "Built by experienced Technocrats and Associates",
        "Legacy of 30+ years in engineering excellence"
      ],
      year: "1987",
      icon: "🏗️"
    },
    {
      id: 2,
      title: "Our Mission",
      content: [
        "Delivering innovative, customer-focused solutions",
        "Providing time-bound, high-quality consultancy",
        "Building relationships through integrity",
        "Creating measurable value through defect-free designs",
        "Ensuring complete customer satisfaction"
      ],
      year: "Mission",
      icon: "🎯"
    },
    {
      id: 3,
      title: "Our Vision",
      content: [
        "Leading professional engineering services provider",
        "Setting industry standards in customer satisfaction",
        "Expanding globally as respected consultants",
        "Fostering lasting relationships through excellence"
      ],
      year: "Vision",
      icon: "🌟"
    },
    {
      id: 4,
      title: "Our Leadership",
      content: [
        "Experienced management team leading innovation",
        "Diverse expertise across engineering domains",
        "Committed to excellence and client success",
        "Strong focus on sustainable development"
      ],
      year: "Present",
      icon: "👥",
      action: {
        text: "Meet Our Management Team",
        link: "/leadership"
      }
    }
  ];

  return (
    <div className="relative max-w-5xl mx-auto">
      <TimelineConnector inView={true} progress={scrollProgress} />
      <div className="space-y-16">
        {timelineData.map((item, index) => (
          <TimelineItem key={item.id} item={item} index={index} />
        ))}
      </div>
    </div>
  );
}

const ContentSection = memo(() => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <section
      ref={ref}
      className={`py-16 md:py-24 transition-all duration-1000 ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <Timeline />
      </div>
    </section>
  );
});

const CareerSection = memo(() => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const careerSections = [
    {
      title: "Life at Ray Infrastructures Pvt. Ltd",
      content: [
        "Get to know our company from inside out and its rich history",
        "Experience our diverse portfolio of projects and meet the people behind them",
        "Work with engineering as our core service",
        "Join a team that believes everything is possible and embraces innovation"
      ]
    },
    {
      title: "Fresh Graduates",
      content: [
        "Join a front-runner in engineering consultancy with 30 years of expertise",
        "Access our archive of expertise and technology",
        "Enhance your engineering knowledge and innovative ideas",
        "Sharpen your engineering talent and showcase it to the world"
      ]
    },
    {
      title: "Experienced Professionals",
      subtitle: "What We Look For",
      content: [
        "Management skills",
        "Leadership abilities",
        "Functional expertise",
        "Problem-solving capabilities"
      ]
    },
    {
      title: "Selection Process",
      content: [
        "Efficient and thorough candidate evaluation",
        "HR team works closely with business sectors",
        "Opportunities across diverse business branches",
        "Focus on long-term career development"
      ]
    }
  ];

  return (
    <section
      ref={ref}
      className="py-16 md:py-24 bg-white/80 backdrop-blur"
    >
      <div className="max-w-7xl mx-auto px-4">
        <h2 className={`text-4xl font-bold text-center text-red-900 mb-12
          transition-all duration-1000 transform
          ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          Beginning a Career With Us
        </h2>

        <div className="grid md:grid-cols-2 gap-8">
          {careerSections.map((section, index) => (
            <div
              key={section.title}
              className={`transition-all duration-1000 transform
                ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
              style={{ transitionDelay: `${index * 200}ms` }}
            >
              <Card inView={inView} index={index} className="h-full">
                <h3 className="text-2xl font-bold text-red-900 mb-4">{section.title}</h3>
                {section.subtitle && (
                  <h4 className="text-lg font-semibold text-red-700 mb-3">{section.subtitle}</h4>
                )}
                <ul className="space-y-3">
                  {section.content.map((item, itemIndex) => (
                    <li
                      key={itemIndex}
                      className="flex items-start gap-3 text-gray-700"
                    >
                      <span className="text-red-600 text-lg mt-1">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          ))}
        </div>

        <div className={`mt-12 text-center transition-all duration-1000 transform
          ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '800ms' }}
        >
          <a
            href="mailto:hr@rayinfra.in"
            className="inline-flex items-center px-8 py-3 bg-red-600 hover:bg-red-700 
              text-white rounded-full font-semibold shadow-lg hover:shadow-xl 
              transition-all duration-300 transform hover:-translate-y-1 hover:scale-105"
          >
            Send Your Resume
          </a>
          <p className="mt-4 text-gray-600">Email: hr@rayinfra.in</p>
        </div>
      </div>
    </section>
  );
});

const CombinedPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100">
      <HeroSection />
      <div className="relative -mt-32 md:-mt-60">
        <ContentSection />
        <CareerSection />
      </div>
    </div>
  );
};

export default CombinedPage;